/* ============================================================
   EDITORIAL PDF ENGINE — shared by the Sales and Stock EOM reports.
   Renders the `.sr` editorial layout off-DOM and paginates content a whole
   row / block at a time onto fixed A4 pages (table headers repeat on spill),
   then rasterises each page into a downloadable multi-page PDF. No content is
   ever split mid-row and every page keeps full, consistent margins.
   Pairs with sales-report-export.css (the shared `.sr` base + paginator rules).
   ============================================================ */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../components/sales-report-export.css';

/* ---------- formatting helpers ---------- */

// Indian-grouped rupee formatting. Passes pre-formatted strings (e.g. "-") through.
export function formatINR(v) {
  if (typeof v === 'string') return v;
  const n = Number(v || 0);
  // A loss reads "−₹1,234" (U+2212 before the symbol), never "₹-1,234".
  // At most two decimals: a per-plate cost of 1000/30 is ₹33.33, not ₹33.333.
  return (n < 0 ? '−₹' : '₹') + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}
export function num(v) {
  if (typeof v === 'string') return v;
  return Number(v || 0).toLocaleString('en-IN');
}
export function pct(v, digits = 1) {
  if (typeof v === 'string') return v;
  return Number(v || 0).toFixed(digits) + '%';
}
export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
export const pad2 = (n) => String(n).padStart(2, '0');

const PATTERNS = ['p-hatch', 'p-dots', 'p-grid', 'p-thin', 'p-solid'];

/* Monochrome pattern-fill bar chart (atomic block). */
export function buildBars(items, scale = 'max') {
  const max = scale === 'hundred'
    ? 100
    : Math.max(1, ...items.map(i => Number(i.value) || 0));
  const rows = items.map((it, i) => {
    const raw = scale === 'hundred' ? (Number(it.value) || 0) : ((Number(it.value) || 0) / max) * 100;
    const w = Math.max(0, Math.min(100, raw));
    const patt = PATTERNS[i % PATTERNS.length];
    return `
      <div class="bar-row">
        <div class="bar-label">${esc(it.label)}</div>
        <div class="bar-track"><div class="bar-fill ${patt}" style="width:${w.toFixed(1)}%"></div></div>
        <div class="bar-value">${esc(it.display)}</div>
      </div>`;
  }).join('');
  const lastTick = scale === 'hundred' ? '100%' : 'Peak';
  return `
    <div class="bars">${rows}</div>
    <div class="axis"><div class="ticks"><span>0</span><span>25%</span><span>50%</span><span>75%</span><span>${lastTick}</span></div></div>`;
}

/* ---------- flow-item factories ----------
   'html'  = an atomic block that must not be split.
   'table' = a data-table that MAY split between rows; its <thead> repeats on
             every continuation page and the <tfoot> stays with the last rows. */
export const htmlItem = (html) => ({ type: 'html', html });
export const tableItem = (thead, rows, tfoot, style) => ({ type: 'table', thead, rows, tfoot: tfoot || '', style: style || '' });

/* ---------- DOM helpers ---------- */

export function mastheadHTML(m) {
  return `<span class="brand">${esc(m.brand)} · ${esc(m.title)}</span><span>${esc(m.periodShort || m.period)}</span>`;
}

function makePage(host, meta, footRight) {
  const page = document.createElement('section');
  page.className = 'page';
  const mh = document.createElement('div');
  mh.className = 'masthead';
  mh.innerHTML = mastheadHTML(meta);
  const flow = document.createElement('div');
  flow.className = 'page-flow';
  const foot = document.createElement('div');
  foot.className = 'pfoot';
  foot.innerHTML = `<span>${esc(meta.generatedBy || 'Generated by Invexis')}</span><span>${esc(footRight || '')}</span>`;
  page.appendChild(mh);
  page.appendChild(flow);
  page.appendChild(foot);
  host.appendChild(page);
  return { page, flow };
}

function makeTableEl(spec) {
  const wrap = document.createElement('div');
  wrap.innerHTML = `<table class="data-table"${spec.style ? ` style="${spec.style}"` : ''}><thead>${spec.thead}</thead><tbody></tbody></table>`;
  return wrap.firstElementChild;
}

/* Available content height inside a section page = A4 content box − masthead − footer. */
function measureAvailableHeight(host, meta) {
  const { page } = makePage(host, meta, '00 / 00');
  const cs = getComputedStyle(page);
  const contentH = page.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  const mh = page.querySelector('.masthead');
  const ft = page.querySelector('.pfoot');
  const mhOuter = mh.offsetHeight + parseFloat(getComputedStyle(mh).marginBottom || 0);
  const ftOuter = ft.offsetHeight;
  host.removeChild(page);
  return Math.max(200, contentH - mhOuter - ftOuter - 8); // 8px safety
}

/* Hand control back to the browser so a long export never freezes the tab
   ("This page isn't responding"). A macrotask lets it paint / process input. */
const yieldToUI = () => new Promise((resolve) => setTimeout(resolve, 0));

/* Lay a section's flow onto one or more A4 pages, never splitting a row and
   repeating table headers on every continuation page. Async so it can yield to
   the UI while paginating large tables. */
async function addSectionPages(host, meta, section, data, availH, numIdx, total) {
  // Sections may override the auto "NN — kicker" header and "NN / total" footer
  // (the channel-based Online report does; Sales/Stock use the defaults).
  const footRight = section.footRight != null
    ? (typeof section.footRight === 'function' ? section.footRight(data) : section.footRight)
    : `${pad2(numIdx)} / ${pad2(total)}`;
  let { flow } = makePage(host, meta, footRight);

  const kickerInner = section.kickerHTML != null
    ? section.kickerHTML
    : `${pad2(numIdx)} &mdash; <b>${esc(section.kicker)}</b>`;
  flow.insertAdjacentHTML('beforeend',
    `<p class="kicker">${kickerInner}</p>` +
    `<h2 class="sec-title${section.titleSm ? ' sm' : ''}">${esc(section.title)}</h2>` +
    `<hr class="rule">` +
    `<p class="lede">${typeof section.lede === 'function' ? section.lede(data) : esc(section.lede)}</p>`);

  const nextPage = () => { flow = makePage(host, meta, footRight).flow; };

  for (const item of section.flow(data)) {
    if (item.type === 'html') {
      flow.insertAdjacentHTML('beforeend', `<div class="flow-blk">${item.html}</div>`);
      const node = flow.lastElementChild;
      if (flow.offsetHeight > availH) {
        flow.removeChild(node);
        nextPage();
        flow.appendChild(node);
      }
    } else {
      let table = makeTableEl(item);
      flow.appendChild(table);
      let tbody = table.querySelector('tbody');

      // Place rows in batches, measuring once per batch. Measuring after EVERY row
      // forces a synchronous reflow per row on a growing table (~O(n²)); on a wide
      // date range that storm of reflows can hang the tab. We only fall back to
      // per-row placement for the one batch that actually crosses a page boundary.
      const BATCH = 20;
      let i = 0, batchNo = 0;
      while (i < item.rows.length) {
        const batch = item.rows.slice(i, i + BATCH);
        const before = tbody.children.length;
        for (const r of batch) tbody.insertAdjacentHTML('beforeend', r);
        if (flow.offsetHeight > availH) {
          // Batch overflowed — roll it back and place its rows one at a time.
          while (tbody.children.length > before) tbody.removeChild(tbody.lastElementChild);
          for (const rowHtml of batch) {
            tbody.insertAdjacentHTML('beforeend', rowHtml);
            const tr = tbody.lastElementChild;
            if (flow.offsetHeight > availH) {
              tbody.removeChild(tr);
              nextPage();
              table = makeTableEl(item);
              flow.appendChild(table);
              tbody = table.querySelector('tbody');
              tbody.appendChild(tr);
            }
          }
        }
        i += batch.length;
        // Yield every ~6 batches (120 rows) so a huge table never blocks the tab.
        if ((++batchNo % 6) === 0) await yieldToUI();
      }

      if (item.tfoot) {
        table.insertAdjacentHTML('beforeend', `<tfoot>${item.tfoot}</tfoot>`);
        if (flow.offsetHeight > availH) {
          const tf = table.querySelector('tfoot');
          table.removeChild(tf);
          nextPage();
          table = makeTableEl(item);
          flow.appendChild(table);
          table.insertAdjacentHTML('beforeend', `<tfoot>${item.tfoot}</tfoot>`);
        }
      }
    }
  }
}

/* Render a full editorial report (cover + contents + numbered sections) and
   download it as a multi-page A4 PDF. Each report supplies its own cover/contents
   HTML and its (already-filtered) section list. */
export async function downloadEditorialReport({ meta, coverHTML, contentsHTML, sections, data, fileName, output }) {
  const host = document.createElement('div');
  host.className = 'sr sr-pdf';
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;background:#ffffff;z-index:-1;pointer-events:none;';
  if (coverHTML) host.insertAdjacentHTML('beforeend', coverHTML);
  if (contentsHTML) host.insertAdjacentHTML('beforeend', contentsHTML);
  document.body.appendChild(host);

  try {
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (e) { /* fall back to system fonts */ }
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const availH = measureAvailableHeight(host, meta);
    // Build each section's pages, yielding between sections so a large export
    // (e.g. a wide date range) never freezes the tab while laying out.
    for (let i = 0; i < sections.length; i++) {
      await addSectionPages(host, meta, sections[i], data, availH, i + 1, sections.length);
      await yieldToUI();
    }

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // compress:true zips the PDF's object streams; images are added as JPEG (far
    // smaller than PNG for these text pages) at a modest scale so the file stays
    // well under 10 MB even for long ranges, while text remains crisp.
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const A4W = 210;
    const A4H = 297;
    const pageEls = Array.from(host.querySelectorAll('.page'));
    // Adapt render scale + JPEG quality to the page count so the file stays under
    // ~10 MB even for a long date range: short reports render crisp, long ones
    // compress harder. (JPEG of white text pages is ~1/10th the size of PNG.)
    const n = pageEls.length;
    const RENDER_SCALE = n > 30 ? 1.4 : (n > 15 ? 1.6 : 1.85);
    const JPEG_QUALITY = n > 30 ? 0.68 : (n > 15 ? 0.74 : 0.82);
    let first = true;
    for (const pageEl of pageEls) {
      await yieldToUI(); // let the browser paint/respond between heavy rasterizations
      const canvas = await html2canvas(pageEl, {
        scale: RENDER_SCALE, useCORS: true, backgroundColor: '#ffffff',
        windowWidth: pageEl.scrollWidth, logging: false, imageTimeout: 0, removeContainer: true,
      });
      if (!first) pdf.addPage();
      first = false;
      pdf.addImage(canvas.toDataURL('image/jpeg', JPEG_QUALITY), 'JPEG', 0, 0, A4W, A4H, undefined, 'FAST');
      // Release the canvas immediately so peak memory stays low across many pages.
      canvas.width = 0; canvas.height = 0;
    }
    // 'blob' → return the PDF as a Blob (for Web Share); default → download it.
    if (output === 'blob') return pdf.output('blob');
    pdf.save(fileName);
    return undefined;
  } finally {
    document.body.removeChild(host);
  }
}
